import { useState } from "react";
import { resetPassword } from "../api/authApi";

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

export default function ResetPasswordModal({ empNo, branchCode, divisionCode, onClose, onSuccess }) {
  const [step, setStep]             = useState(1);
  const [phoneLast4, setPhoneLast4] = useState("");
  const [form, setForm]             = useState({ newPassword: "", confirmPassword: "" });
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [showPw, setShowPw]         = useState({ new: false, confirm: false });
  

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const togglePw = (k) => setShowPw(p => ({ ...p, [k]: !p[k] }));

  // 1단계 : 본인 확인 + 전화번호 검증
  const handleVerify = async () => {
    setError("");

    if (!phoneLast4 || !/^\d{4}$/.test(phoneLast4)) {
      setError("전화번호 뒷 4자리를 올바르게 입력해주세요."); return;
    }

    setLoading(true);
    try {
      // ✅ 1단계에서 바로 전화번호 검증 API 호출
      const res = await fetch(`${BASE_URL}/auth/verify-phone`, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ 
            emp_no: empNo, 
            phone_last4: phoneLast4,
            branch_code : branchCode,   
            division_code: divisionCode 
        })
      });
      const data = await res.json();

      if (data.success) {
        setStep(2);  // ← 검증 성공 시 2단계로 이동
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 2단계 : 새 비밀번호 설정
  const handleSubmit = async () => {
    setError("");

    if (!form.newPassword || !form.confirmPassword) {
      setError("모든 항목을 입력해주세요."); return;
    }
    if (form.newPassword.length < 8) {
      setError("비밀번호는 8자리 이상이어야 합니다."); return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("새 비밀번호가 일치하지 않습니다."); return;
    }

    setLoading(true);
    try {
      const res = await resetPassword(
        empNo,
        phoneLast4,
        form.newPassword,
        form.confirmPassword,
        branchCode,
        divisionCode
      );
      if (res.success) {
        onSuccess();
      } else {
        setStep(1);
        setError(res.message);
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* 헤더 */}
        <div style={styles.header}>
          <span style={styles.title}>🔑 비밀번호 찾기</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* 스텝 표시 */}
        <div style={styles.stepWrap}>
          <div style={{ ...styles.step, ...(step === 1 ? styles.stepActive : styles.stepDone) }}>
            1. 본인 확인
          </div>
          <div style={styles.stepArrow}>›</div>
          <div style={{ ...styles.step, ...(step === 2 ? styles.stepActive : {}) }}>
            2. 비밀번호 설정
          </div>
        </div>

        {/* 바디 */}
        <div style={styles.body}>
          {step === 1 ? (
            <>
              <div style={styles.notice}>
                💡 가입 시 등록한 전화번호 뒷 4자리로 본인 확인을 합니다.
              </div>

              {/* 사번 - 수정 불가 */}
              <div style={styles.field}>
                <label style={styles.label}>사번</label>
                <input
                  style={{ ...styles.input, background: "#f0f0f0", color: "#aaa" }}
                  value={empNo}
                  disabled
                />
              </div>

              {/* 전화번호 뒷 4자리 */}
              <div style={styles.field}>
                <label style={styles.label}>
                  전화번호 뒷 4자리 <span style={{ color: "#ff4d4f" }}>*</span>
                </label>
                <input
                  style={styles.input}
                  placeholder="숫자 4자리 입력"
                  maxLength={4}
                  value={phoneLast4}
                  onChange={e => setPhoneLast4(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </>
          ) : (
            <>
              <div style={styles.notice}>
                💡 비밀번호는 8자리 이상 입력해주세요.
              </div>

              {/* 사번 - 수정 불가 */}
              <div style={styles.field}>
                <label style={styles.label}>사번</label>
                <input
                  style={{ ...styles.input, background: "#f0f0f0", color: "#aaa" }}
                  value={empNo}
                  disabled
                />
              </div>

              {[
                { label: "새 비밀번호",      key: "newPassword",     pwKey: "new"     },
                { label: "새 비밀번호 확인", key: "confirmPassword", pwKey: "confirm" },
              ].map(f => (
                <div key={f.key} style={styles.field}>
                  <label style={styles.label}>
                    {f.label} <span style={{ color: "#ff4d4f" }}>*</span>
                  </label>
                  <div style={styles.inputWrap}>
                    <input
                      style={styles.input}
                      type={showPw[f.pwKey] ? "text" : "password"}
                      placeholder={f.label}
                      value={form[f.key]}
                      onChange={e => set(f.key, e.target.value)}
                    />
                    <span style={styles.eyeIcon} onClick={() => togglePw(f.pwKey)}>
                      {showPw[f.pwKey] ? "🙈" : "👁"}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* 오류 메시지 */}
          {error && <div style={styles.errorBox}>⚠️ {error}</div>}
        </div>

        {/* 푸터 */}
        <div style={styles.footer}>
          {step === 2 && (
            <button
              style={styles.prevBtn}
              onClick={() => { setStep(1); setError(""); }}
            >
              ← 이전
            </button>
          )}
          <button style={styles.cancelBtn} onClick={onClose}>취소</button>
          {step === 1 ? (
            <button
              style={{ ...styles.saveBtn, opacity: loading ? 0.7 : 1 }}
              onClick={handleVerify}
              disabled={loading}
            >
              {loading ? "확인 중..." : "다음 →"}
            </button>
          ) : (
            <button
              style={{ ...styles.saveBtn, opacity: loading ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "처리 중..." : "비밀번호 변경"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay   : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" },
  modal     : { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 420, margin: "0 16px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" },
  header    : { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  title     : { fontSize: 16, fontWeight: 700, color: "#1a1a2e" },
  closeBtn  : { background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" },
  stepWrap  : { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 24px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" },
  step      : { fontSize: 13, fontWeight: 600, color: "#bbb", padding: "4px 12px", borderRadius: 20 },
  stepActive: { background: "#e8f4fd", color: "#1677ff" },
  stepDone  : { background: "#f6ffed", color: "#52c41a" },
  stepArrow : { fontSize: 18, color: "#ccc" },
  body      : { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  field     : { display: "flex", flexDirection: "column", gap: 6 },
  label     : { fontSize: 13, fontWeight: 600, color: "#444" },
  inputWrap : { position: "relative", display: "flex", alignItems: "center" },
  input     : { width: "100%", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 40px 10px 12px", fontSize: 14, outline: "none", background: "#fafafa", color: "#1a1a2e", boxSizing: "border-box" },
  eyeIcon   : { position: "absolute", right: 12, fontSize: 16, cursor: "pointer", userSelect: "none" },
  notice    : { background: "#f0f7ff", border: "1px solid #bae0ff", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1677ff" },
  errorBox  : { background: "#fff5f5", border: "1px solid #ffcccc", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#c0392b" },
  footer    : { display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid #f0f0f0" },
  prevBtn   : { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 16px", fontSize: 14, color: "#666", cursor: "pointer", marginRight: "auto" },
  cancelBtn : { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#666", cursor: "pointer" },
  saveBtn   : { background: "linear-gradient(135deg, #1677ff, #4096ff)", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#fff", fontWeight: 700, cursor: "pointer" },
};